import { trackRumAction } from '@/shared/observability/rum';
import { clearFpQaHandoff } from '@/shared/utils/fpQaHandoff';
import type { MatchedSpecialtyApi } from '../navigator-matching.types';
import { normalizeCategories } from '../selection';
import {
  createFpEngagement,
  prefetchHubResources,
  prefetchSuggestedResources,
  runJourneySummaryChain,
} from './generationSteps';
import { runMatchingPipeline } from './matchingPipeline';
import type {
  FpEngagementInput,
  GenerationMode,
  GenerationPhase,
  NavigatorGenerationState,
  StartGenerationOptions,
} from './navigator-generation.types';

// Module-level store: generation must survive route changes (chat → loading →
// results), so it lives outside component state. Consumed via
// useNavigatorGeneration (useSyncExternalStore) or getGenerationState.
const states = new Map<string, NavigatorGenerationState>();
// FP engagement inputs kept out of the public state so a failed create can be
// retried with the original payload.
const engagementInputs = new Map<string, FpEngagementInput>();
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

export const subscribeToGeneration = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getGenerationState = (
  sessionId?: string,
): NavigatorGenerationState | undefined => (sessionId ? states.get(sessionId) : undefined);

const createState = (
  sessionId: string,
  mode: GenerationMode,
  phase: GenerationPhase,
): NavigatorGenerationState => ({
  sessionId,
  mode,
  status: 'running',
  phase,
  categories: [],
  matchingProgress: null,
  engagementId: null,
  engagementError: false,
  startedAt: Date.now(),
});

const patch = (sessionId: string, partial: Partial<NavigatorGenerationState>) => {
  const current = states.get(sessionId);
  if (!current) return;
  states.set(sessionId, { ...current, ...partial });
  emit();
};

/**
 * Feature 080 — replace the categories snapshot (selected-first normalized).
 * Exported for swapSelection so the optimistic update + revert live outside
 * this file without exposing the whole patch surface.
 */
export const patchCategories = (sessionId: string, categories: MatchedSpecialtyApi[]): void => {
  patch(sessionId, { categories: normalizeCategories(categories) });
};

const PHASE_ORDER: GenerationPhase[] = [
  'summary',
  'care-needs',
  'searching',
  'matching',
  'resources',
  'writing',
  'done',
];

/** Phases only move forward — pipeline callbacks can fire out of order. */
const advancePhase = (sessionId: string, phase: GenerationPhase) => {
  const current = states.get(sessionId);
  if (!current) return;
  if (PHASE_ORDER.indexOf(phase) <= PHASE_ORDER.indexOf(current.phase)) return;
  patch(sessionId, { phase });
};

/** /care-options resolved the specialty list — the earliest "total" signal. */
const reportMatchingTotal = (sessionId: string, total: number) => {
  const current = states.get(sessionId);
  if (!current || total <= 0) return;
  const matched = current.matchingProgress?.matched ?? 0;
  patch(sessionId, {
    matchingProgress: { matched, total: Math.max(current.matchingProgress?.total ?? 0, total) },
  });
  advancePhase(sessionId, 'searching');
};

/**
 * Mid-poll/terminal categories snapshot: the backend completes one specialty
 * search at a time, so specialties-with-providers is a real progress counter.
 * Drives searching (list known) → matching (first providers landed).
 */
const applyCategoriesSnapshot = (sessionId: string, categories: MatchedSpecialtyApi[]) => {
  const current = states.get(sessionId);
  if (!current) return;
  const total = Math.max(categories.length, current.matchingProgress?.total ?? 0);
  const matched = categories.filter((specialty) => specialty.providers.length > 0).length;
  patch(sessionId, {
    categories: normalizeCategories(categories),
    ...(total > 0 ? { matchingProgress: { matched, total } } : {}),
  });
  if (total > 0) advancePhase(sessionId, 'searching');
  if (matched > 0) advancePhase(sessionId, 'matching');
};

const runFpEngagementCreate = async (sessionId: string, input: FpEngagementInput) => {
  engagementInputs.set(sessionId, input);
  try {
    const engagementId = await createFpEngagement(sessionId, input);
    patch(sessionId, { engagementId, engagementError: false });
    // Only a successful create consumes the handoff — the FP results live on
    // the dashboard, keyed by this engagement.
    clearFpQaHandoff();
  } catch (error) {
    console.error('[Navigator] Failed to create FP engagement:', error);
    patch(sessionId, { engagementError: true });
  }
};

export const retryFpEngagement = (sessionId: string): void => {
  const input = engagementInputs.get(sessionId);
  const state = states.get(sessionId);
  if (!input || !state || state.engagementId) return;
  patch(sessionId, { engagementError: false });
  void runFpEngagementCreate(sessionId, input);
};

const finishMatching = (sessionId: string, ok: boolean, categories: MatchedSpecialtyApi[]) => {
  const state = states.get(sessionId);
  patch(sessionId, {
    status: ok ? 'ready' : 'error',
    phase: 'done',
    categories: normalizeCategories(categories),
  });
  trackRumAction('care_guide_generation_ready', {
    sessionId,
    ok,
    durationMs: state ? Date.now() - state.startedAt : undefined,
  });
};

/**
 * Full generation run, kicked from the chat the moment the intake completes —
 * BEFORE the user clicks "Generate Care Guide" (the button is a visual
 * affordance; the work is already underway). Idempotent per session, so the
 * loading page re-kicking on mount is a no-op.
 *
 * Ordering: the journey-summary chain writes care_plans.conversation_summary
 * (the AI-ranking input), so it must settle before /care-options kicks the
 * matchmaking pipeline. The FP engagement create is independent and runs in
 * parallel.
 *
 * Resource prefetches are deliberately NOT started up front: hub resources
 * are keyed off `navigator_matchmaking_searches` rows that /care-options only
 * persists fire-and-forget (the HTTP response doesn't wait for that write —
 * see `care-options.controller.ts`). Prefetching before `onReadySignal` raced
 * that write and cached an empty bundle under a `staleTime: Infinity` key —
 * permanently hiding the "Read guide" card for the whole session, since the
 * results page's own query would then treat that empty cache as fresh forever.
 * `onReadySignal` only fires once the backend reports READY, which guarantees
 * the search rows already exist.
 */
export const startGeneration = (sessionId: string, options: StartGenerationOptions): void => {
  if (states.has(sessionId)) return;
  states.set(sessionId, createState(sessionId, options.mode, 'summary'));
  emit();
  trackRumAction('care_guide_generation_started', { sessionId, mode: options.mode });

  if (options.fpEngagement) {
    void runFpEngagementCreate(sessionId, options.fpEngagement);
  }

  const run = async () => {
    try {
      await runJourneySummaryChain(sessionId);
    } catch (error) {
      // Non-fatal: matching falls back to specialty-name queries, as today.
      console.warn('[Navigator] Journey summary failed (continuing):', error);
    }
    advancePhase(sessionId, 'care-needs');

    const result = await runMatchingPipeline(sessionId, {
      onCareOptions: (total) => reportMatchingTotal(sessionId, total),
      onCategories: (categories) => applyCategoriesSnapshot(sessionId, categories),
      onReadySignal: () => {
        advancePhase(sessionId, 'resources');
        // Not awaited: `onReadySignal` is itself awaited by the matching
        // pipeline's terminalLoad BEFORE it loads the final specialties and
        // persists the care plan — blocking here would tack non-critical
        // cache-warming network calls onto the critical path. 'writing' is a
        // cosmetic phase label, so it just advances best-effort once the
        // prefetches settle (a no-op if 'done' already landed first).
        const prefetches = [prefetchHubResources(sessionId, options.mode).catch(() => undefined)];
        if (options.mode === 'care-seeker') {
          prefetches.push(prefetchSuggestedResources(sessionId).catch(() => undefined));
        }
        void Promise.allSettled(prefetches).then(() => advancePhase(sessionId, 'writing'));
      },
    });
    finishMatching(sessionId, result.ok, result.categories);
  };
  void run();
};

/**
 * Attach-or-start for results surfaces (useNavigatorMatching) and the loading
 * page after a reload: if the chat already ran the full generation this is a
 * no-op; otherwise (deep link, reload, hub revisit) it runs the matching-only
 * pipeline — the pre-store useNavigatorMatching behavior. It never re-runs the
 * journey-summary chain (an LLM call that would regenerate the plan narrative
 * on every results visit).
 *
 * `fpEngagement`, when supplied (the loading page's reload-recovery path —
 * the in-memory store was wiped by the reload, so `engagementId` is gone),
 * re-runs the engagement create. That call is idempotent on
 * `navigatorSessionId` server-side, so this safely recovers the existing
 * engagement id rather than creating a duplicate.
 */
export const attachGeneration = (
  sessionId: string,
  mode: GenerationMode = 'care-seeker',
  fpEngagement?: FpEngagementInput,
): void => {
  if (states.has(sessionId)) return;
  states.set(sessionId, createState(sessionId, mode, 'care-needs'));
  emit();

  if (mode === 'fp' && fpEngagement) {
    void runFpEngagementCreate(sessionId, fpEngagement);
  }

  const run = async () => {
    const result = await runMatchingPipeline(sessionId, {
      onCareOptions: (total) => reportMatchingTotal(sessionId, total),
      onCategories: (categories) => applyCategoriesSnapshot(sessionId, categories),
      onReadySignal: () => advancePhase(sessionId, 'writing'),
    });
    finishMatching(sessionId, result.ok, result.categories);
  };
  void run();
};

/**
 * Test-only: the module-level store persists across tests in a file — reset it
 * in beforeEach. A dangling pipeline from a previous test no-ops on patch()
 * because its state entry is gone. Emits so any still-subscribed component
 * (useSyncExternalStore) re-renders to the cleared snapshot immediately,
 * instead of holding a stale one until the next patch().
 */
export const resetNavigatorGeneration = (): void => {
  states.clear();
  engagementInputs.clear();
  emit();
};
